import React, { createContext, useContext, useEffect, useState } from 'react';
import { supabase, checkSupabaseConnection, createNetworkMonitor } from '../lib/supabase';

const AuthContext = createContext({});

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [userProfile, setUserProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [profileLoading, setProfileLoading] = useState(false);
  const [connectionStatus, setConnectionStatus] = useState({ isConnected: true, error: null });
  const [retryAttempts, setRetryAttempts] = useState(0);
  const [networkStatus, setNetworkStatus] = useState({ isOnline: navigator?.onLine ?? true });
  const [authInitialized, setAuthInitialized] = useState(false);

  // Enhanced null-safe fallback profile creation
  const createFallbackProfile = (userId, user, reason = 'error') => {
    // CRITICAL FIX: Add comprehensive null checks to prevent prototype errors
    const safeUserId = userId || user?.id || 'unknown';
    const safeEmail = user?.email || '';
    const safeUserMetadata = user?.user_metadata || {};
    const safeFullName = safeUserMetadata?.full_name || safeEmail?.split('@')?.[0] || 'User';
    
    return {
      id: safeUserId, // CRITICAL: Must match auth.uid() for RLS policies to work
      email: safeEmail,
      full_name: safeFullName,
      organizations: [],
      organization_id: null,
      hasOrganizations: false,
      isFallback: true,
      fallbackReason: reason,
      created_at: new Date()?.toISOString(),
      updated_at: new Date()?.toISOString(),
      is_active: true,
      role: 'org_user',
      avatar_url: safeUserMetadata?.avatar_url || null
    };
  };

  // Network monitoring with enhanced error handling
  useEffect(() => {
    let cleanup;
    
    try {
      cleanup = createNetworkMonitor((status) => {
        if (status && typeof status === 'object') {
          setNetworkStatus(status);
          if (status?.isOnline) {
            setRetryAttempts(0);
          }
        }
      });
    } catch (error) {
      console.error('Network monitor initialization failed:', error);
      // Set fallback network status
      setNetworkStatus({ isOnline: navigator?.onLine ?? true });
    }
    
    return () => {
      try {
        cleanup?.();
      } catch (error) {
        console.error('Network monitor cleanup failed:', error);
      }
    };
  }, []);

  // Enhanced connection monitoring with better error handling
  const monitorConnection = async () => {
    try {
      if (!networkStatus?.isOnline) {
        setConnectionStatus({ 
          isConnected: false, 
          error: { message: 'No internet connection', isNetworkError: true } 
        });
        return;
      }

      const status = await checkSupabaseConnection();
      
      // CRITICAL FIX: Add null checks for status object
      if (status && typeof status === 'object') {
        setConnectionStatus(status);
        
        if (!status?.isConnected && retryAttempts < 3) {
          setRetryAttempts(prev => prev + 1);
          const delay = Math.min(5000 * Math.pow(2, retryAttempts), 30000);
          setTimeout(() => monitorConnection(), delay);
        } else if (status?.isConnected) {
          setRetryAttempts(0);
        }
      }
    } catch (error) {
      console.error('Connection monitoring failed:', error);
      setConnectionStatus({ 
        isConnected: false, 
        error: { message: 'Connection check failed', isNetworkError: true } 
      });
    }
  };

  // Connection monitoring effect with error handling
  useEffect(() => {
    const timeout = setTimeout(() => {
      try {
        monitorConnection();
      } catch (error) {
        console.error('Monitor connection failed:', error);
      }
    }, 1000);
    
    return () => clearTimeout(timeout);
  }, [networkStatus?.isOnline]);

  // Manual retry function for UI components
  const retryConnection = async () => {
    try {
      setRetryAttempts(0);
      await monitorConnection();
    } catch (error) {
      console.error('Retry connection failed:', error);
    }
  };

  // Enhanced profile operations with comprehensive error handling
  const profileOperations = {
    async load(userId) {
      if (!userId) {
        setUserProfile(null);
        setProfileLoading(false);
        return;
      }

      let loadingTimeout;
      const maxLoadingTime = 8000;

      try {
        setProfileLoading(true);

        // Set a timeout to prevent infinite loading - ALWAYS provide fallback
        loadingTimeout = setTimeout(() => {
          console.warn('Profile loading timeout - providing fallback profile');
          const fallbackProfile = createFallbackProfile(userId, user, 'timeout');
          setUserProfile(fallbackProfile);
          setProfileLoading(false);
        }, maxLoadingTime);

        // Immediate fallback for offline scenarios
        if (!networkStatus?.isOnline) {
          clearTimeout(loadingTimeout);
          const fallbackProfile = createFallbackProfile(userId, user, 'offline');
          setUserProfile(fallbackProfile);
          setProfileLoading(false);
          return;
        }

        // Step 1: Enhanced session validation with null checks
        let currentSession = null;
        try {
          // CRITICAL FIX: Add comprehensive null checks for supabase object
          if (!supabase || !supabase?.auth) {
            throw new Error('Supabase client not properly initialized');
          }

          const sessionResult = await supabase?.auth?.getSession();
          
          // CRITICAL FIX: Add null checks for session result
          if (!sessionResult || !sessionResult?.data) {
            throw new Error('Invalid session result structure');
          }

          const { data: sessionData, error: sessionError } = sessionResult;
          
          if (sessionError) {
            console.warn('Session retrieval error:', sessionError);
            throw new Error('Session invalid - need to re-authenticate');
          }
          
          currentSession = sessionData?.session;
          
          if (!currentSession || !currentSession?.user || currentSession?.user?.id !== userId) {
            console.error('Session mismatch or invalid:', { 
              hasSession: !!currentSession, 
              sessionUserId: currentSession?.user?.id, 
              expectedUserId: userId 
            });
            throw new Error('Authentication session invalid or mismatched');
          }
        } catch (sessionErr) {
          console.error('Session validation failed:', sessionErr);
          clearTimeout(loadingTimeout);
          const fallbackProfile = createFallbackProfile(userId, user, 'session_error');
          fallbackProfile.errorMessage = 'Authentication session expired. Please sign out and sign in again.';
          setUserProfile(fallbackProfile);
          setProfileLoading(false);
          return;
        }

        // Step 2: Enhanced profile loading with better error handling
        let profile = null;
        try {
          console.debug('Loading user profile for ID:', userId);
          
          // CRITICAL FIX: Add null checks for supabase operations
          if (!supabase || !supabase?.from) {
            throw new Error('Supabase client not available for database operations');
          }

          const profileQuery = supabase?.from('user_profiles')?.select('*')?.eq('id', userId)?.maybeSingle();

          // CRITICAL FIX: Add null check for query result
          if (!profileQuery) {
            throw new Error('Failed to create profile query');
          }

          const profileResult = await profileQuery;
          
          // CRITICAL FIX: Add comprehensive null checks
          if (!profileResult) {
            throw new Error('Profile query returned null result');
          }

          const { data: profileData, error: profileError } = profileResult;

          if (profileError) {
            console.warn('Profile fetch error:', profileError);
            
            // Handle profile not found - create profile with better error handling
            if (profileError?.code === 'PGRST116' || profileError?.message?.includes('0 rows') || !profileData) {
              console.info('Creating new user profile for:', userId);
              try {
                // CRITICAL FIX: Use upsert to handle race conditions and duplicates
                const insertResult = await supabase?.from('user_profiles')?.upsert({
                    id: userId,
                    email: currentSession?.user?.email || '',
                    full_name: currentSession?.user?.user_metadata?.full_name || currentSession?.user?.email?.split('@')?.[0] || 'User',
                    created_at: new Date()?.toISOString(),
                    updated_at: new Date()?.toISOString(),
                    is_active: true,
                    role: 'org_user'
                  }, {
                    onConflict: 'id', // Handle conflicts on the primary key
                    ignoreDuplicates: false // Return the existing record if duplicate
                  })?.select()?.single();
                  
                // CRITICAL FIX: Add null checks for insert result
                if (!insertResult) {
                  throw new Error('Upsert operation returned null result');
                }

                const { data: newProfile, error: createError } = insertResult;
                
                if (createError) {
                  // If it's still a duplicate error, try to fetch the existing profile
                  if (createError?.code === '23505' || createError?.message?.includes('duplicate key')) {
                    console.info('Profile already exists due to race condition, fetching existing profile');
                    const existingProfileResult = await supabase?.from('user_profiles')?.select('*')?.eq('id', userId)?.single();
                    
                    if (existingProfileResult?.data) {
                      profile = existingProfileResult?.data;
                      console.info('Successfully fetched existing profile:', profile?.id);
                    } else {
                      throw new Error('Profile creation failed and existing profile fetch failed: ' + createError?.message);
                    }
                  } else {
                    console.error('Profile creation failed:', createError);
                    throw new Error('Profile creation failed: ' + createError?.message);
                  }
                } else {
                  profile = newProfile;
                  console.info('Successfully created/updated user profile:', profile?.id);
                }
              } catch (createErr) {
                console.error('Profile creation error:', createErr);
                
                // Final fallback: try to fetch existing profile one more time
                if (createErr?.message?.includes('duplicate key') || createErr?.code === '23505') {
                  console.info('Duplicate key error - attempting to fetch existing profile as fallback');
                  try {
                    const fallbackResult = await supabase?.from('user_profiles')?.select('*')?.eq('id', userId)?.single();
                    if (fallbackResult?.data) {
                      profile = fallbackResult?.data;
                      console.info('Successfully fetched existing profile after duplicate error:', profile?.id);
                    } else {
                      throw new Error('Profile creation failed and fallback fetch failed - ' + createErr?.message);
                    }
                  } catch (fallbackErr) {
                    throw new Error('Profile creation failed with duplicate error and fallback failed - ' + createErr?.message);
                  }
                } else {
                  throw new Error('Profile creation failed - ' + createErr?.message);
                }
              }
            } else {
              throw new Error('Profile fetch error: ' + profileError?.message);
            }
          } else if (profileData) {
            profile = profileData;
            console.debug('Successfully loaded user profile:', profile?.id);
          } else {
            console.info('No profile found, creating new one for:', userId);
            throw new Error('Profile not found - need to create');
          }
        } catch (profileErr) {
          console.error('Profile operation failed, using fallback:', profileErr);
          clearTimeout(loadingTimeout);
          const fallbackProfile = createFallbackProfile(userId, user, 'profile_error');
          fallbackProfile.errorMessage = profileErr?.message;
          setUserProfile(fallbackProfile);
          setProfileLoading(false);
          return;
        }

        // Step 3: Enhanced organization loading with better error handling
        let userOrganizations = [];
        let organizationError = null;

        try {
          console.debug('Loading user organizations for:', userId);
          
          // CRITICAL FIX: Add null checks for RPC call
          if (!supabase || !supabase?.rpc) {
            throw new Error('Supabase RPC not available');
          }

          const rpcResult = await supabase?.rpc('get_user_organizations');
          
          // CRITICAL FIX: Add null checks for RPC result
          if (!rpcResult) {
            throw new Error('RPC call returned null result');
          }

          const { data: orgFunctionData, error: orgFunctionError } = rpcResult;

          if (orgFunctionError) {
            console.warn('Organization function call failed, trying direct query:', orgFunctionError);
            
            // Fallback to direct query with better error handling
            const membershipResult = await supabase?.from('organization_members')?.select(`organization_id,role,joined_at,organization:organizations(id,name,slug,status,settings)`)?.eq('user_id', userId);

            // CRITICAL FIX: Add null checks for membership result
            if (membershipResult) {
              const { data: membershipData, error: membershipError } = membershipResult;

              if (membershipError) {
                console.warn('Organization membership fetch error (non-critical):', membershipError);
                organizationError = membershipError;
              } else if (membershipData && Array.isArray(membershipData) && membershipData?.length > 0) {
                userOrganizations = membershipData?.map(membership => ({
                  organization_id: membership?.organization_id,
                  role: membership?.role,
                  joined_at: membership?.joined_at,
                  ...membership?.organization
                }));
                console.debug('Successfully loaded organizations via direct query:', userOrganizations?.length);
              }
            }
          } else if (orgFunctionData && Array.isArray(orgFunctionData) && orgFunctionData?.length > 0) {
            // Process function result format with null checks
            userOrganizations = orgFunctionData?.map(org => ({
              organization_id: org?.organization_id,
              id: org?.organization_id,
              name: org?.organization_name,
              role: org?.role,
              joined_at: org?.joined_at,
              slug: `org-${org?.organization_id?.substring(0, 8)}`,
              status: 'active',
              settings: {}
            }));
            console.debug('Successfully loaded organizations via function:', userOrganizations?.length);
          }
        } catch (orgError) {
          console.warn('Organization loading failed (non-critical):', orgError);
          organizationError = orgError;
        }

        // Step 4: Create enriched profile with null checks
        const enrichedProfile = {
          ...profile,
          organizations: userOrganizations || [],
          organization_id: userOrganizations?.[0]?.organization_id || userOrganizations?.[0]?.id || null,
          hasOrganizations: Array.isArray(userOrganizations) && userOrganizations?.length > 0,
          organizationError: organizationError,
          isFallback: false,
          authSession: !!currentSession
        };

        clearTimeout(loadingTimeout);
        setUserProfile(enrichedProfile);
        console.debug('Profile loading completed successfully with organizations:', userOrganizations?.length || 0);
        
      } catch (error) {
        clearTimeout(loadingTimeout);
        console.error('Profile loading failed - using fallback:', error);
        
        // ALWAYS provide a fallback profile - never leave user hanging
        let fallbackReason = 'error';
        if (error?.message?.includes('Failed to fetch') || error?.message?.includes('NetworkError')) {
          fallbackReason = 'network_error';
        } else if (error?.message?.includes('401') || error?.message?.includes('Unauthorized') || error?.message?.includes('session')) {
          fallbackReason = 'auth_error';
        } else if (error?.message?.includes('timeout')) {
          fallbackReason = 'timeout';
        }
        
        const fallbackProfile = createFallbackProfile(userId, user, fallbackReason);
        fallbackProfile.errorMessage = error?.message;
        setUserProfile(fallbackProfile);
      } finally {
        if (loadingTimeout) clearTimeout(loadingTimeout);
        setProfileLoading(false);
      }
    },

    clear() {
      setUserProfile(null);
      setProfileLoading(false);
    }
  };

  // Enhanced auth state handlers with comprehensive error handling
  const authStateHandlers = {
    onChange: (event, session) => {
      try {
        console.debug('Auth state change:', event, session?.user?.id || 'no user');
        
        if (event === 'SIGNED_OUT' || (!session && event !== 'INITIAL_SESSION')) {
          setUser(null);
          profileOperations?.clear();
          setLoading(false);
          setAuthInitialized(true);
          return;
        }

        if (session?.user) {
          setUser(session?.user);
          setLoading(false);
          setAuthInitialized(true);
          
          // Load profile immediately with timeout protection
          if (!userProfile || userProfile?.id !== session?.user?.id) {
            profileOperations?.load(session?.user?.id);
          }
          
          // Handle offline scenarios with immediate fallback
          if (!networkStatus?.isOnline) {
            const minimalProfile = createFallbackProfile(session?.user?.id, session?.user, 'offline');
            setUserProfile(minimalProfile);
          }
        } else if (event === 'INITIAL_SESSION') {
          setUser(null);
          profileOperations?.clear();
          setLoading(false);
          setAuthInitialized(true);
        }
      } catch (error) {
        console.error('Auth state change handler failed:', error);
        setUser(null);
        profileOperations?.clear();
        setLoading(false);
        setAuthInitialized(true);
      }
    }
  };

  // Enhanced auth initialization with comprehensive error handling
  useEffect(() => {
    let initTimeout;
    let mounted = true;

    const initializeAuth = async () => {
      try {
        console.debug('Initializing auth...');
        
        // Set a maximum initialization time
        initTimeout = setTimeout(() => {
          if (mounted) {
            console.warn('Auth initialization timeout - assuming no session');
            setUser(null);
            setLoading(false);
            setAuthInitialized(true);
          }
        }, 6000);
        
        if (!networkStatus?.isOnline) {
          console.debug('Offline - skipping auth initialization');
          if (mounted) {
            setLoading(false);
            setAuthInitialized(true);
          }
          return;
        }

        // CRITICAL FIX: Add comprehensive null checks for supabase
        if (!supabase || !supabase?.auth) {
          console.error('Supabase client not available during auth initialization');
          if (mounted) {
            setLoading(false);
            setAuthInitialized(true);
          }
          return;
        }

        const sessionResult = await supabase?.auth?.getSession();
        
        if (!mounted) return;
        
        // CRITICAL FIX: Add null checks for session result
        if (!sessionResult || !sessionResult?.data) {
          console.warn('Invalid session result during initialization');
          if (mounted) {
            setLoading(false);
            setAuthInitialized(true);
          }
          return;
        }

        const { data: sessionData, error } = sessionResult;
        
        if (error) {
          console.warn('Initial session check error:', error);
        }
        
        if (initTimeout) {
          clearTimeout(initTimeout);
        }
        
        authStateHandlers?.onChange('INITIAL_SESSION', sessionData?.session);
      } catch (error) {
        console.error('Auth initialization failed:', error);
        if (mounted) {
          setLoading(false);
          setAuthInitialized(true);
        }
      }
    };

    // Only initialize once when component mounts
    initializeAuth();

    // CRITICAL FIX: Add null checks for auth subscription
    let subscription;
    try {
      if (supabase && supabase?.auth && supabase?.auth?.onAuthStateChange) {
        const subscriptionResult = supabase?.auth?.onAuthStateChange(authStateHandlers?.onChange);
        subscription = subscriptionResult?.data;
      }
    } catch (error) {
      console.error('Auth state change subscription failed:', error);
    }

    return () => {
      mounted = false;
      if (initTimeout) clearTimeout(initTimeout);
      try {
        subscription?.unsubscribe();
      } catch (error) {
        console.error('Auth subscription cleanup failed:', error);
      }
    };
  }, []);

  // Enhanced auth methods with comprehensive error handling
  const signIn = async (email, password) => {
    try {
      // CRITICAL FIX: Add input validation
      if (!email || !password) {
        return { 
          error: { message: 'Please provide both email and password.' } 
        };
      }

      // CRITICAL FIX: Validate email format
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex?.test(email)) {
        return { 
          error: { message: 'Please provide a valid email address.' } 
        };
      }

      if (!networkStatus?.isOnline) {
        return { 
          error: { message: 'No internet connection. Please check your network and try again.' } 
        };
      }

      if (!supabase || !supabase?.auth) {
        return { 
          error: { message: 'Authentication service not available. Please try again later.' } 
        };
      }

      // CRITICAL FIX: Enhanced error handling for authentication
      const result = await supabase?.auth?.signInWithPassword({ 
        email: email?.trim()?.toLowerCase(), 
        password: password 
      });
      
      if (!result) {
        return { 
          error: { message: 'Sign in failed. Please try again.' } 
        };
      }

      const { data, error } = result;
      
      // CRITICAL FIX: Enhanced error message handling
      if (error) {
        let errorMessage = 'Sign in failed. Please try again.';
        
        if (error?.message?.includes('Invalid login credentials')) {
          errorMessage = 'Invalid email or password. Please check your credentials and try again.';
        } else if (error?.message?.includes('Email not confirmed')) {
          errorMessage = 'Please confirm your email address before signing in.';
        } else if (error?.message?.includes('Account locked')) {
          errorMessage = 'Your account has been locked. Please contact support.';
        } else if (error?.message?.includes('Too many requests')) {
          errorMessage = 'Too many sign-in attempts. Please wait a few minutes and try again.';
        } else if (error?.status === 400) {
          errorMessage = 'Invalid credentials or request format. Please check your email and password.';
        } else if (error?.status === 429) {
          errorMessage = 'Too many requests. Please wait a moment and try again.';
        } else if (error?.message?.includes('Failed to fetch') || error?.message?.includes('NetworkError')) {
          errorMessage = 'Network connection error. Please check your internet connection and try again.';
        }
        
        console.warn('Sign in error:', error);
        return { error: { message: errorMessage, originalError: error } };
      }
      
      if (data?.user) {
        console.debug('Sign in successful for:', email);
      }
      
      return { data, error: null };
    } catch (error) {
      console.error('Sign in exception:', error);
      
      let errorMessage = 'An unexpected error occurred. Please try again.';
      
      if (error?.message?.includes('Failed to fetch') || error?.message?.includes('NetworkError')) {
        errorMessage = 'Network connection error. Please check your internet connection and try again.';
      } else if (error?.message?.includes('400')) {
        errorMessage = 'Invalid credentials format. Please check your email and password.';
      } else if (error?.message?.includes('timeout')) {
        errorMessage = 'Request timed out. Please try again.';
      }
      
      return { 
        error: { 
          message: errorMessage,
          isNetworkError: error?.message?.includes('Failed to fetch') || error?.message?.includes('NetworkError')
        } 
      };
    }
  };

  const signOut = async () => {
    try {
      // CRITICAL FIX: Always clear local state first
      setUser(null);
      profileOperations?.clear();

      // CRITICAL FIX: Check if supabase is available
      if (!supabase || !supabase?.auth) {
        console.debug('Supabase not available, signed out locally');
        return { error: null };
      }

      // CRITICAL FIX: Check if there's an active session before attempting sign out
      let hasActiveSession = false;
      try {
        const { data: sessionData } = await supabase?.auth?.getSession();
        hasActiveSession = !!(sessionData?.session?.user);
      } catch (sessionError) {
        console.debug('Session check failed, proceeding with local signout:', sessionError);
        return { error: null };
      }

      // Only attempt server signout if there's an active session
      if (hasActiveSession) {
        console.debug('Active session found, signing out from server...');
        const result = await supabase?.auth?.signOut();
        
        // CRITICAL FIX: Handle sign out result properly
        const error = result?.error || null;
        
        if (error) {
          // Log the error but don't prevent local signout console.warn('Server sign out warning (local signout still successful):', error);
          
          // Handle specific "Auth session missing" error gracefully
          if (error?.message?.includes('Auth session missing') || error?.name === 'AuthSessionMissingError') {
            console.debug('Session already cleared on server side, local signout completed');
            return { error: null };
          }
          
          // For other errors, still consider signout successful locally
          return { 
            error: null, 
            warning: 'Signed out locally. Server signout had an issue but this does not affect your security.' 
          };
        }
        
        console.debug('Sign out successful from server');
      } else {
        console.debug('No active session found, local signout completed');
      }
      
      return { error: null };
    } catch (error) {
      console.error('Sign out error:', error);
      
      // CRITICAL FIX: Handle AuthSessionMissingError specifically
      if (error?.message?.includes('Auth session missing') || error?.name === 'AuthSessionMissingError') {
        console.debug('Auth session already missing, signout completed locally');
        return { error: null };
      }
      
      // For network or other errors, local signout is still successful
      const isNetworkError = error?.message?.includes('Failed to fetch') || error?.message?.includes('NetworkError');
      
      return { 
        error: null,
        warning: isNetworkError 
          ? 'Network error during sign out, but you have been signed out locally.' :'Sign out completed locally. Server signout had an issue but this does not affect your security.'
      };
    }
  };

  const updateProfile = async (updates) => {
    try {
      if (!user) return { error: { message: 'No user logged in' } };
      
      if (!networkStatus?.isOnline) {
        return { 
          error: { message: 'No internet connection. Please check your network and try again.' } 
        };
      }
      
      // CRITICAL FIX: Add null checks for supabase
      if (!supabase || !supabase?.from) {
        return { 
          error: { message: 'Database service not available. Please try again later.' } 
        };
      }
      
      const result = await supabase?.from('user_profiles')?.update({
          ...updates,
          updated_at: new Date()?.toISOString()
        })?.eq('id', user?.id)?.select()?.single();
      
      // CRITICAL FIX: Add null checks for update result
      if (!result) {
        return { 
          error: { message: 'Profile update failed. Please try again.' } 
        };
      }

      const { data, error } = result;
      
      if (!error) {
        setUserProfile(prevProfile => ({ ...prevProfile, ...data }));
        console.debug('Profile updated successfully');
      }
      
      return { data, error };
    } catch (error) {
      const isNetworkError = error?.message?.includes('Failed to fetch');
      console.error('Profile update error:', error);
      return { 
        error: { 
          message: isNetworkError 
            ? 'Network connection error. Please check your internet connection and try again.' :'Profile update failed. Please try again.'
        } 
      };
    }
  };

  // CRITICAL FIX: Add comprehensive null checks for context value
  const value = {
    user: user || null,
    userProfile: userProfile || null,
    loading: loading ?? true,
    profileLoading: profileLoading ?? false,
    connectionStatus: connectionStatus || { isConnected: true, error: null },
    networkStatus: networkStatus || { isOnline: true },
    authInitialized: authInitialized ?? false,
    retryConnection,
    signIn,
    signOut,
    updateProfile,
    isAuthenticated: !!user,
    isOnline: networkStatus?.isOnline ?? true,
    hasOrganization: !!(userProfile?.hasOrganizations && !userProfile?.isFallback),
    organizationId: userProfile?.organization_id || null
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export { AuthContext };